// Reusable "Drive files" block (Phase 5): chips for attached Drive files with
// remove, a Google Picker add flow, and a paste-a-link fallback. Pure w.r.t.
// persistence — calls onSave(nextLinks) with the complete next array; each
// host surface wires that to its own updater. SSR-safe: the Picker loads only
// on click, never at render.
import { useState } from 'react'
import { useGoogle } from '../lib/useGoogle.js'
import { driveShowPicker } from '../lib/google.js'

const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
  border: '1px solid var(--color-divider-dark)', borderRadius: 999,
  fontSize: 12, color: 'var(--color-white)', textDecoration: 'none',
  maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
const ghostBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', fontSize: 12, padding: '4px 6px' }

export default function DriveLinksField({ links = [], onSave, legacyUrl = null }) {
  const g = useGoogle()
  const [pasting, setPasting] = useState(false)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const remove = (i) => onSave(links.filter((_, j) => j !== i))

  const addPasted = () => {
    const u = url.trim()
    if (!u) return
    // http(s) only — a javascript:/data: href would be stored junk another
    // workspace user can click.
    if (!/^https?:\/\//i.test(u)) {
      setError('Links must start with http:// or https://')
      return
    }
    setError(null)
    onSave([...links, { fileId: null, name: name.trim() || u, url: u, mimeType: null }])
    setName(''); setUrl(''); setPasting(false)
  }

  const pick = async () => {
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      const docs = await driveShowPicker()
      const have = new Set(links.map((l) => l.fileId).filter(Boolean))
      const fresh = docs.filter((d) => !have.has(d.fileId))
      if (fresh.length) onSave([...links, ...fresh])
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="tag-label" style={{ color: 'var(--color-muted)', marginBottom: 8 }}>Drive files</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {links.map((l, i) => (
          <span key={`${l.url}-${i}`} style={chip} title={l.name}>
            <a href={l.url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-white)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</a>
            <button onClick={() => remove(i)} title="Remove" style={{ ...ghostBtn, padding: 0 }}>✕</button>
          </span>
        ))}
        {legacyUrl && (
          <a href={legacyUrl} target="_blank" rel="noreferrer" style={{ ...chip, color: 'var(--color-muted)' }} title="Legacy link (from import)">Legacy link</a>
        )}
        <button onClick={pick} disabled={busy} className="btn btn-ghost" style={{ fontSize: 11, padding: '5px 10px', opacity: busy ? 0.45 : 1 }}>
          {busy ? 'Opening…' : '+ Drive file'}
        </button>
        {!pasting && (
          <button onClick={() => setPasting(true)} style={ghostBtn}>+ Paste link</button>
        )}
      </div>
      {pasting && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="cn-input" style={{ fontSize: 12, padding: '7px 10px', width: 140 }} />
          <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPasted()} placeholder="https://…" className="cn-input" style={{ fontSize: 12, padding: '7px 10px', flex: 1 }} />
          <button onClick={addPasted} className="btn btn-gold" style={{ fontSize: 11, padding: '7px 12px' }}>Add</button>
          <button onClick={() => { setPasting(false); setName(''); setUrl('') }} style={ghostBtn}>✕</button>
        </div>
      )}
      {g.status !== 'connected' && !error && (
        <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 6 }}>Connect Google in Settings to pick from Drive; pasted links always work.</div>
      )}
      {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>{error}</div>}
    </div>
  )
}
